import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import API_BASE_URL from "../config/Config";
import { PageLoader } from "./ui";
import { formatDate } from "../utils/formatters";

const PostDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/posts/${id}`)
      .then((res) => {
        setPost(res.data.data);
      })
      .catch((err) => {
        console.error("Ошибка при загрузке поста", err);
        setError("Не удалось загрузить новость");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const mediaFiles = useMemo(() => {
    if (!post) return [];

    const images = [...(post.image_urls || [])];
    if (post.image_url) images.unshift(post.image_url);

    const videos = [...(post.video_urls || [])];
    return [
      ...images.map((url) => ({ type: "image", url })),
      ...videos.map((url) => ({ type: "video", url })),
    ];
  }, [post]);

  useEffect(() => {
    if (mediaFiles.length > 0) {
      setSelectedMedia(mediaFiles[0]);
    }
  }, [mediaFiles]);

  const handleCommentSubmit = async () => {
    if (!token || !commentText.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/posts/${id}/comments`,
        { text: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCommentText("");
      const res = await axios.get(`${API_BASE_URL}/posts/${id}`);
      setPost(res.data.data);
    } catch (err) {
      console.error("Ошибка при добавлении комментария", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error || !post) {
    return (
      <section className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center bg-dark-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center bg-white p-8 rounded-2xl shadow-card max-w-md"
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-dark-700 text-lg mb-6">{error || "Новость не найдена"}</p>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-teal-500 text-white font-semibold rounded-xl"
          >
            Вернуться к новостям
          </Link>
        </motion.div>
      </section>
    );
  }

  const comments = post.comments || [];

  return (
    <section className="min-h-screen pt-28 pb-20 bg-dark-50">
      {/* Back button */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 text-dark-500 hover:text-primary-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Назад к новостям
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-card overflow-hidden"
        >
          <div className="flex flex-col lg:flex-row">
            {/* Media Section */}
            <div className="lg:w-1/2">
              {/* Main Media */}
              <div className="relative h-[400px] lg:h-[500px] bg-dark-100">
                {selectedMedia ? (
                  selectedMedia.type === "image" ? (
                    <img
                      src={selectedMedia.url}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      src={selectedMedia.url}
                      controls
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-dark-400">
                    <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {mediaFiles.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto">
                  {mediaFiles.map((media, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedMedia(media)}
                      className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedMedia?.url === media.url
                          ? "border-primary-500 ring-2 ring-primary-500/30"
                          : "border-transparent hover:border-dark-200"
                      }`}
                    >
                      {media.type === "image" ? (
                        <img src={media.url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-dark-200 flex items-center justify-center">
                          <svg className="w-6 h-6 text-dark-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Content Section */}
            <div className="lg:w-1/2 p-6 lg:p-8 flex flex-col">
              {/* Date */}
              <div className="mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary-50 text-primary-700 text-sm font-medium rounded-lg">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(post.created_at)}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-2xl lg:text-3xl font-bold text-dark-900 mb-6">
                {post.title}
              </h1>

              {/* Text */}
              <div className="prose prose-lg text-dark-600 flex-1">
                <p className="whitespace-pre-line leading-relaxed">{post.text}</p>
              </div>

              {/* Share */}
              <div className="mt-6 pt-6 border-t border-dark-100">
                <p className="text-sm text-dark-500 mb-3">Поделиться:</p>
                <div className="flex gap-2">
                  <a
                    href={`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-dark-100 text-dark-500 hover:bg-primary-500 hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-dark-100 text-dark-500 hover:bg-green-500 hover:text-white transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.article>

        {/* Comments Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 bg-white rounded-2xl shadow-card p-6 lg:p-8"
        >
          <h2 className="text-xl font-bold text-dark-900 mb-6 flex items-center gap-2">
            <svg className="w-6 h-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Комментарии ({comments.length})
          </h2>

          {comments.length === 0 ? (
            <p className="text-dark-400 text-center py-8">Пока нет комментариев. Будьте первым!</p>
          ) : (
            <div className="space-y-4 mb-6">
              {comments.map((comment) => (
                <div key={comment.id} className="p-4 bg-dark-50 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-teal-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {comment.user?.username?.[0]?.toUpperCase() || "?"}
                    </div>
                    <span className="font-semibold text-dark-900">{comment.user?.username || "Аноним"}</span>
                  </div>
                  <p className="text-dark-600 pl-11">{comment.text}</p>
                </div>
              ))}
            </div>
          )}

          {token ? (
            <div className="border-t border-dark-100 pt-6">
              <textarea
                className="w-full border border-dark-200 rounded-xl p-4 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                rows={3}
                placeholder="Напишите комментарий..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
              <div className="flex justify-end mt-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCommentSubmit}
                  disabled={submitting || !commentText.trim()}
                  className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-teal-500 text-white font-semibold rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Отправка..." : "Отправить"}
                </motion.button>
              </div>
            </div>
          ) : (
            <div className="border-t border-dark-100 pt-6 text-center">
              <p className="text-dark-500 mb-3">Войдите, чтобы оставить комментарий</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-dark-900 text-white font-medium rounded-xl hover:bg-dark-800 transition-colors"
              >
                Войти
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default PostDetail;
